import {
  ElementTagsChipListBase,
  useReadableElementTags,
} from "@/components/ui/tags/element-tags-chip-list";
import { TagsMenu } from "@/components/ui/tags/tags-menu";
import { Tag } from "@/store/tags/tags-slice";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  FaroButton,
  FaroButtonProps,
  FilterMenuOptionType,
  TagsAddIcon,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import Stack from "@mui/material/Stack";
import { SxProps } from "@mui/system";
import { useCallback, useState, type JSX } from "react";

type EditElementTagsBaseProps = {
  /** The element to edit the tags for */
  tags?: Tag[];

  /** Whether to use dark mode styles */
  dark?: boolean;

  /** Styles to apply to the container */
  sx?: SxProps;

  /** Whether to disable the menu and show a loading spinner. */
  isLoading?: boolean;

  /** Whether managing tags should be allowed. @default true */
  allowManageTags?: boolean;

  /** The allowed tags for selection. */
  projectTags: Tag[];

  /** Option elements for the allowed tags. */
  tagOptions: FilterMenuOptionType[];

  /**
   * Whether to hide the chips inside the component
   *
   * @default false
   */
  hideTagsChips?: boolean;

  /** Additional props to pass to the assign tag button */
  buttonProps?: Partial<FaroButtonProps>;

  /**
   * Callback when the user confirms the tag selection. Is called with the selected tags, and the available tag options.
   */
  onAssignTags(
    selectedTagOptions: Tag[],
    tagOptions: FilterMenuOptionType[],
  ): Promise<void> | void;
};

/**
 * @returns Base component for editing tags of an element. Only provides the input UI, without sending any mutations.
 */
export function EditElementTagsBase({
  tags,
  sx,
  dark,
  isLoading,
  allowManageTags,
  projectTags,
  tagOptions,
  hideTagsChips = false,
  buttonProps,
  onAssignTags,
}: EditElementTagsBaseProps): JSX.Element {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isAssignTagOpen, setIsAssignTagOpen] = useState(false);

  const [selectedTagOptions, setSelectedTagOptions] = useState<
    FilterMenuOptionType[]
  >(() => []);

  // Initialize the list of selected tags, any time the menu is opened.
  useNonExhaustiveEffect(() => {
    setSelectedTagOptions(
      tagOptions.filter((option) => tags?.some((tag) => tag.id === option.key)),
    );
  }, [isAssignTagOpen]);

  // Make sure deleted tags are removed from the selection.
  useNonExhaustiveEffect(() => {
    setSelectedTagOptions((prevSelectedOptions) =>
      prevSelectedOptions.filter((selectedOption) =>
        tagOptions.some((option) => option.key === selectedOption.key),
      ),
    );
  }, [projectTags]);

  const mapOptionToTag = useCallback(
    (option: FilterMenuOptionType) => {
      const tag = projectTags.find((tag) => tag.id === option.key);
      assert(tag, "Selected tag option not found in labels");
      return tag;
    },
    [projectTags],
  );

  const assignTags = useCallback(async () => {
    await onAssignTags(selectedTagOptions.map(mapOptionToTag), tagOptions);
    setIsAssignTagOpen(false);
  }, [mapOptionToTag, onAssignTags, selectedTagOptions, tagOptions]);

  const hasTags = !!useReadableElementTags(tags)?.length;

  return (
    <Stack
      direction="row"
      sx={[
        {
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {
        // Don't render empty list, to prevent a gap in the layout
        hasTags && !hideTagsChips && (
          <ElementTagsChipListBase
            tags={tags}
            disabled={isLoading}
            dark={dark}
            // Since we are already inside a menu (=modal in MUI), the popper needs to be shown above it
            popperZIndex={(theme) => theme.zIndex.modal + 1}
            onRemoveTagFromElement={(removeTag) =>
              onAssignTags(
                (tags ?? []).filter((t) => t.id !== removeTag.id),
                tagOptions,
              )
            }
          />
        )
      }
      <FaroButton
        ref={setMenuAnchorEl}
        variant="ghost"
        size="xs"
        dark={dark}
        icon={<TagsAddIcon sx={{ transform: "scale(1.5)" }} />}
        sx={{ whiteSpace: "nowrap" }}
        onClick={(ev) => {
          setIsAssignTagOpen(true);
          ev.stopPropagation();
        }}
        {...buttonProps}
      >
        Assign tag
      </FaroButton>

      {isAssignTagOpen && (
        <TagsMenu
          title="Assign tags"
          isOpen
          anchorEl={menuAnchorEl}
          onClose={() => setIsAssignTagOpen(false)}
          options={tagOptions}
          value={selectedTagOptions}
          onChange={setSelectedTagOptions}
          dark={dark}
          disabled={isLoading}
          allowManageTags={allowManageTags}
        >
          <FaroButton
            onClick={assignTags}
            isLoading={isLoading}
            dark={dark}
            size="m"
          >
            Assign
          </FaroButton>
        </TagsMenu>
      )}
    </Stack>
  );
}
